import { useTranslations } from "use-intl";
import { useLaunchSplashHold } from "@/lib/launch-splash";
import { cn } from "@/lib/utils";

const HEIGHT_CLASS = {
  dvh: "h-dvh",
  full: "h-full",
} as const;

/**
 * A CSS ring rather than the lucide-backed `<Spinner>`: the `page` variant is
 * what the pre-login and lazy-chunk fallbacks render, and none of the modules
 * on that path import `lucide-react`. Keep it icon-free so the boot chunk
 * stays small.
 *
 * `page` is the full-bleed boot/route state: a large ring on an opaque
 * backdrop, so nothing shows through while a chunk or a session resolves.
 * `inline` sits inside a surface that already paints its own background, so it
 * stays transparent and uses the lighter ring.
 */
const VARIANT = {
  inline: {
    backdrop: "",
    ring: "h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent",
  },
  page: {
    backdrop: "bg-background",
    ring: "h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-foreground",
  },
} as const;

type Variant = keyof typeof VARIANT;

/**
 * The ring plus its caption, for a loading state that sits inside an existing
 * layout. `LoadingScreen` wraps this for the full-bleed case.
 */
export function LoadingMessage({ caption }: { caption: string }) {
  return (
    <div className="text-center" data-testid="loading-screen" role="status">
      <div className={cn("mx-auto mb-4", VARIANT.page.ring)} />
      <p className="text-muted-foreground text-sm">{caption}</p>
    </div>
  );
}

/**
 * Centered loading state filling its container. `height` picks between the
 * viewport (`dvh`, for a boot or a route-level Suspense fallback) and the
 * parent box (`full`, for a tab or panel that already occupies its slot).
 */
export function LoadingScreen({
  caption,
  height = "dvh",
  variant = "page",
}: {
  caption?: string;
  height?: keyof typeof HEIGHT_CLASS;
  variant?: Variant;
}) {
  const t = useTranslations("common");
  useLaunchSplashHold();
  const { backdrop, ring } = VARIANT[variant];
  return (
    <div
      className={cn(
        "flex items-center justify-center",
        HEIGHT_CLASS[height],
        backdrop
      )}
    >
      {caption === undefined ? (
        <div
          aria-label={t("loading")}
          className={ring}
          data-testid="loading-screen"
          role="status"
        />
      ) : (
        <LoadingMessage caption={caption} />
      )}
    </div>
  );
}
